import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import {
  Activity,
  ArrowLeft,
  Clock,
  Zap,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronRight,
  Code,
  FileText,
  Image,
  Download,
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';

// ============================================
// Types
// ============================================

interface Trace {
  id: string;
  orgId?: string;
  projectId?: string;
  userId?: string;
  requestId?: string;
  type: string;
  status: 'running' | 'success' | 'error' | 'cancelled';
  startedAt: string;
  endedAt?: string;
  durationMs?: number;
  totalSpans: number;
  totalEvents: number;
  totalArtifacts: number;
  llmCalls?: number;
  totalInputTokens?: number;
  totalOutputTokens?: number;
  errorMessage?: string;
  errorCode?: string;
  agentVersion?: string;
  templateVersion?: string;
  modelName?: string;
  confidence?: number;
  tags?: string[];
}

interface Span {
  id: string;
  traceId: string;
  parentSpanId?: string;
  name: string;
  source: string;
  startedAt: string;
  endedAt?: string;
  durationMs?: number;
  status: 'running' | 'success' | 'error';
  errorMessage?: string;
  metrics?: Record<string, number>;
  childSpanCount: number;
  eventCount: number;
}

interface TelemetryEvent {
  eventId: string;
  timestamp: string;
  traceId: string;
  spanId?: string;
  source: string;
  level: 'error' | 'warn' | 'info' | 'debug' | 'trace';
  eventType: string;
  name: string;
  message?: string;
  tags?: string[];
  data?: Record<string, unknown>;
  metrics?: Record<string, number>;
}

interface Artifact {
  id: string;
  traceId: string;
  spanId?: string;
  type: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  preview?: string;
  createdAt: string;
}

interface TraceDetails {
  trace: Trace;
  spans: Span[];
  events: TelemetryEvent[];
  artifacts: Artifact[];
}

// ============================================
// Constants
// ============================================

const STATUS_CONFIG = {
  running: { icon: RefreshCw, class: 'text-blue-400 animate-spin', bg: 'bg-blue-500/20' },
  success: { icon: CheckCircle, class: 'text-green-400', bg: 'bg-green-500/20' },
  error: { icon: XCircle, class: 'text-red-400', bg: 'bg-red-500/20' },
  cancelled: { icon: AlertCircle, class: 'text-yellow-400', bg: 'bg-yellow-500/20' },
};

const LEVEL_CONFIG = {
  error: { class: 'text-red-400', bg: 'bg-red-500/10' },
  warn: { class: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  info: { class: 'text-blue-400', bg: 'bg-blue-500/10' },
  debug: { class: 'text-surface-400', bg: 'bg-surface-700' },
  trace: { class: 'text-surface-500', bg: 'bg-surface-800' },
};

const ARTIFACT_ICONS: Record<string, typeof Image> = {
  screenshot: Image,
  dom_snippet: Code,
  a11y_tree_raw: Code,
  a11y_tree_summary: FileText,
  prompt_full: FileText,
  response_full: FileText,
  focused_tree: Code,
};

// ============================================
// Component
// ============================================

export function TraceDetailPage() {
  const { orgId, projectId, traceId } = useParams<{ orgId: string; projectId: string; traceId: string }>();
  const [activeTab, setActiveTab] = useState<'timeline' | 'events' | 'artifacts'>('timeline');
  const [selectedSpan, setSelectedSpan] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [eventLevelFilter, setEventLevelFilter] = useState<string>('');
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['trace', traceId],
    queryFn: () => api.get<TraceDetails>(`/telemetry/traces/${traceId}`),
    enabled: !!traceId,
    refetchInterval: (query) => query.state.data?.trace.status === 'running' ? 2000 : false,
  });
  
  const formatDuration = (ms?: number) => {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };
  
  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  };
  
  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Build span tree
  const buildSpanTree = (spans: Span[]) => {
    const rootSpans = spans.filter(s => !s.parentSpanId);
    const childMap = new Map<string, Span[]>();
    
    spans.forEach(span => {
      if (span.parentSpanId) {
        const children = childMap.get(span.parentSpanId) || [];
        children.push(span);
        childMap.set(span.parentSpanId, children);
      }
    });
    
    return { rootSpans, childMap };
  };
  
  // Filter events
  const filteredEvents = data?.events.filter(e => 
    !eventLevelFilter || e.level === eventLevelFilter
  ) || [];
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="h-32 bg-surface-700 rounded" />
        </div>
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-surface-300">Trace not found</p>
      </div>
    );
  }
  
  const { trace, spans, events, artifacts } = data;
  const StatusIcon = STATUS_CONFIG[trace.status].icon;
  const { rootSpans, childMap } = buildSpanTree(spans);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/app/organizations/${orgId}/projects/${projectId}/telemetry`}
          className="p-2 rounded-lg hover:bg-surface-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-surface-400" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold text-surface-100 flex items-center gap-2">
            <Activity className="w-6 h-6 text-accent-400" />
            Trace Details
          </h1>
          <p className="text-surface-400 font-mono text-sm">{trace.id}</p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-secondary"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>
      
      {/* Trace Summary Card */}
      <div className="card">
        <div className="flex items-start gap-6">
          {/* Status */}
          <div className={clsx('p-4 rounded-xl', STATUS_CONFIG[trace.status].bg)}>
            <StatusIcon className={clsx('w-8 h-8', STATUS_CONFIG[trace.status].class)} />
          </div>
          
          {/* Info */}
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <div className="text-surface-500 text-xs tracking-wider mb-1">Type</div>
              <div className="text-surface-100 font-medium">{trace.type.replace(/_/g, ' ')}</div>
            </div>
            <div>
              <div className="text-surface-500 text-xs tracking-wider mb-1">Status</div>
              <div className={clsx('font-medium', STATUS_CONFIG[trace.status].class)}>
                {trace.status}
              </div>
            </div>
            <div>
              <div className="text-surface-500 text-xs tracking-wider mb-1">Duration</div>
              <div className="text-surface-100 font-medium flex items-center gap-1">
                <Clock className="w-4 h-4 text-surface-500" />
                {formatDuration(trace.durationMs)}
              </div>
            </div>
            <div>
              <div className="text-surface-500 text-xs tracking-wider mb-1">Tokens</div>
              <div className="text-surface-100 font-medium flex items-center gap-1">
                <Zap className="w-4 h-4 text-surface-500" />
                {((trace.totalInputTokens || 0) + (trace.totalOutputTokens || 0)).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-surface-500 text-xs tracking-wider mb-1">Model</div>
              <div className="text-surface-100 font-medium">{trace.modelName || '—'}</div>
            </div>
            <div>
              <div className="text-surface-500 text-xs tracking-wider mb-1">Template</div>
              <div className="text-surface-100 font-medium">{trace.templateVersion || '—'}</div>
            </div>
            <div>
              <div className="text-surface-500 text-xs tracking-wider mb-1">Confidence</div>
              <div className={clsx(
                'font-medium',
                trace.confidence !== undefined ? (
                  trace.confidence >= 0.8 ? 'text-green-400' :
                  trace.confidence >= 0.6 ? 'text-yellow-400' : 'text-red-400'
                ) : 'text-surface-500'
              )}>
                {trace.confidence !== undefined ? `${Math.round(trace.confidence * 100)}%` : '—'}
              </div>
            </div>
            <div>
              <div className="text-surface-500 text-xs tracking-wider mb-1">Started</div>
              <div className="text-surface-100 text-sm">{new Date(trace.startedAt).toLocaleString()}</div>
            </div>
          </div>
        </div>
        
        {/* Error message */}
        {trace.errorMessage && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm font-mono">{trace.errorMessage}</p>
          </div>
        )}
        
        {/* Request link */}
        {trace.requestId && (
          <div className="mt-4 pt-4 border-t border-surface-700">
            <Link
              to={`/app/organizations/${orgId}/projects/${projectId}/requests/${trace.requestId}`}
              className="text-accent-400 hover:text-accent-300 text-sm flex items-center gap-1"
            >
              View Request <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-700">
        <button
          onClick={() => setActiveTab('timeline')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'timeline'
              ? 'border-accent-400 text-accent-400'
              : 'border-transparent text-surface-400 hover:text-surface-200'
          )}
        >
          Timeline ({spans.length} spans)
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'events'
              ? 'border-accent-400 text-accent-400'
              : 'border-transparent text-surface-400 hover:text-surface-200'
          )}
        >
          Events ({events.length})
        </button>
        <button
          onClick={() => setActiveTab('artifacts')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'artifacts'
              ? 'border-accent-400 text-accent-400'
              : 'border-transparent text-surface-400 hover:text-surface-200'
          )}
        >
          Artifacts ({artifacts.length})
        </button>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'timeline' && (
        <div className="card">
          <div className="space-y-1">
            {rootSpans.map(span => (
              <SpanRow
                key={span.id}
                span={span}
                childMap={childMap}
                depth={0}
                selectedSpan={selectedSpan}
                onSelect={setSelectedSpan}
                formatDuration={formatDuration}
                formatTime={formatTime}
              />
            ))}
          </div>
        </div>
      )}
      
      {activeTab === 'events' && (
        <div className="space-y-4">
          {/* Level filter */}
          <div className="flex gap-2">
            <select
              value={eventLevelFilter}
              onChange={(e) => setEventLevelFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="">All Levels</option>
              <option value="error">Error</option>
              <option value="warn">Warning</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
              <option value="trace">Trace</option>
            </select>
          </div>
          
          <div className="card divide-y divide-surface-700">
            {filteredEvents.map(event => (
              <div
                key={event.eventId}
                className={clsx(
                  'p-3 cursor-pointer hover:bg-surface-750 transition-colors',
                  selectedEvent === event.eventId && 'bg-surface-750'
                )}
                onClick={() => setSelectedEvent(
                  selectedEvent === event.eventId ? null : event.eventId
                )}
              >
                <div className="flex items-start gap-3">
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded text-xs font-medium',
                    LEVEL_CONFIG[event.level].bg,
                    LEVEL_CONFIG[event.level].class
                  )}>
                    {event.level}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-surface-100 font-medium">{event.name}</span>
                      <span className="text-surface-500 text-xs">{event.eventType}</span>
                    </div>
                    {event.message && (
                      <p className="text-surface-400 text-sm mt-0.5 line-clamp-2">
                        {event.message}
                      </p>
                    )}
                    
                    {/* Expanded data */}
                    {selectedEvent === event.eventId && event.data && Object.keys(event.data).length > 0 && (
                      <div className="mt-3 p-3 bg-surface-800 rounded-lg">
                        <pre className="text-xs text-surface-300 overflow-x-auto">
                          {JSON.stringify(event.data, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    {/* Metrics */}
                    {selectedEvent === event.eventId && event.metrics && Object.keys(event.metrics).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(event.metrics).map(([key, value]) => (
                          <span key={key} className="px-2 py-1 bg-surface-700 rounded text-xs">
                            <span className="text-surface-500">{key}:</span>{' '}
                            <span className="text-surface-300">{value}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-surface-500 text-xs whitespace-nowrap">
                    {formatTime(event.timestamp)}
                  </span>
                </div>
              </div>
            ))}
            
            {filteredEvents.length === 0 && (
              <div className="text-center py-8 text-surface-500">
                No events found
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'artifacts' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {artifacts.map(artifact => {
            const Icon = ARTIFACT_ICONS[artifact.type] || FileText;
            
            return (
              <div key={artifact.id} className="card p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-surface-700 rounded-lg">
                    <Icon className="w-5 h-5 text-surface-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-surface-100 font-medium">{artifact.type}</div>
                    <div className="text-surface-500 text-xs mt-0.5">
                      {artifact.contentType} • {formatBytes(artifact.sizeBytes)}
                    </div>
                    {artifact.preview && (
                      <div className="mt-2 p-2 bg-surface-750 rounded text-xs text-surface-400 line-clamp-3 font-mono">
                        {artifact.preview}
                      </div>
                    )}
                  </div>
                  <a
                    href={`/api/v1/telemetry/artifacts/${artifact.id}`}
                    download
                    className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                    title="Download"
                  >
                    <Download className="w-4 h-4 text-surface-400" />
                  </a>
                </div>
              </div>
            );
          })}
          
          {artifacts.length === 0 && (
            <div className="col-span-full text-center py-12 text-surface-500">
              No artifacts captured for this trace
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Span Row Component (recursive)
// ============================================

function SpanRow({
  span,
  childMap,
  depth,
  selectedSpan,
  onSelect,
  formatDuration,
  formatTime,
}: {
  span: Span;
  childMap: Map<string, Span[]>;
  depth: number;
  selectedSpan: string | null;
  onSelect: (id: string | null) => void;
  formatDuration: (ms?: number) => string;
  formatTime: (ts: string) => string;
}) {
  const children = childMap.get(span.id) || [];
  const StatusIcon = STATUS_CONFIG[span.status]?.icon || CheckCircle;
  const isSelected = selectedSpan === span.id;
  
  return (
    <>
      <div
        className={clsx(
          'flex items-center gap-2 py-2 px-3 rounded cursor-pointer hover:bg-surface-750 transition-colors',
          isSelected && 'bg-surface-750'
        )}
        style={{ paddingLeft: `${12 + depth * 20}px` }}
        onClick={() => onSelect(isSelected ? null : span.id)}
      >
        {children.length > 0 && (
          <ChevronRight className={clsx(
            'w-4 h-4 text-surface-500 transition-transform',
            isSelected && 'rotate-90'
          )} />
        )}
        {children.length === 0 && <div className="w-4" />}
        
        <StatusIcon className={clsx('w-4 h-4', STATUS_CONFIG[span.status]?.class || 'text-surface-400')} />
        
        <span className="text-surface-100 font-medium flex-1">{span.name}</span>
        
        <span className="text-surface-500 text-xs px-2 py-0.5 bg-surface-700 rounded">
          {span.source}
        </span>
        
        <span className="text-surface-400 text-sm w-20 text-right">
          {formatDuration(span.durationMs)}
        </span>
        
        <span className="text-surface-500 text-xs w-28 text-right">
          {formatTime(span.startedAt)}
        </span>
      </div>
      
      {/* Show children when selected */}
      {isSelected && children.map(child => (
        <SpanRow
          key={child.id}
          span={child}
          childMap={childMap}
          depth={depth + 1}
          selectedSpan={selectedSpan}
          onSelect={onSelect}
          formatDuration={formatDuration}
          formatTime={formatTime}
        />
      ))}
      
      {/* Show metrics when selected */}
      {isSelected && span.metrics && Object.keys(span.metrics).length > 0 && (
        <div
          className="py-2 px-3 bg-surface-750"
          style={{ paddingLeft: `${32 + depth * 20}px` }}
        >
          <div className="flex flex-wrap gap-2">
            {Object.entries(span.metrics).map(([key, value]) => (
              <span key={key} className="px-2 py-1 bg-surface-700 rounded text-xs">
                <span className="text-surface-500">{key}:</span>{' '}
                <span className="text-surface-300">{value}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Show error when selected */}
      {isSelected && span.errorMessage && (
        <div
          className="py-2 px-3"
          style={{ paddingLeft: `${32 + depth * 20}px` }}
        >
          <div className="p-2 bg-red-500/10 border border-red-500/20 rounded">
            <p className="text-red-400 text-sm font-mono">{span.errorMessage}</p>
          </div>
        </div>
      )}
    </>
  );
}

